import { useState, useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from "@/components/ui/accordion";
import { 
  Collapsible, 
  CollapsibleContent, 
  CollapsibleTrigger 
} from "@/components/ui/collapsible";
import { ChevronDown, Edit3, List, Loader2, Plus, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { useIsAdmin } from "@/hooks/useUserRole";
import { cn } from "@/lib/utils";
import {
  TERMS_CATEGORIES as FALLBACK_TERMS_CATEGORIES,
  type TermsCategory,
} from "@/lib/termsClausesData";

interface TermsAndConditionsSelectorProps {
  value: string;
  onChange: (value: string) => void;
}

export function TermsAndConditionsSelector({ 
  value, 
  onChange 
}: TermsAndConditionsSelectorProps) {
  const qc = useQueryClient();
  const { isAdmin } = useIsAdmin();
  const { data: categories = FALLBACK_TERMS_CATEGORIES, isLoading } = useQuery({
    queryKey: ["terms-library"],
    queryFn: async (): Promise<TermsCategory[]> => {
      const { data: cats, error: catErr } = await (supabase as any)
        .from("terms_categories")
        .select("id, name, slug, sort_order, is_active")
        .eq("is_active", true)
        .order("sort_order", { ascending: true });
      if (catErr) throw catErr;
      const { data: clauses, error: clErr } = await (supabase as any)
        .from("terms_clauses")
        .select("id, category_id, text, sort_order, is_active, is_default")
        .eq("is_active", true)
        .order("sort_order", { ascending: true });
      if (clErr) throw clErr;
      return (cats ?? []).map((c: any) => ({
        id: c.id,
        name: c.name,
        clauses: (clauses ?? [])
          .filter((cl: any) => cl.category_id === c.id)
          .map((cl: any) => ({ id: cl.id, text: cl.text, isDefault: cl.is_default })),
      }));
    },
    staleTime: 60_000,
  });

  const invalidateLibrary = () => {
    qc.invalidateQueries({ queryKey: ["terms-library"] });
    qc.invalidateQueries({ queryKey: ["admin-terms-categories"] });
    qc.invalidateQueries({ queryKey: ["admin-terms-clauses"] });
  };

  const [newClauseTexts, setNewClauseTexts] = useState<Record<string, string>>({});
  const [newCategoryName, setNewCategoryName] = useState("");
  const [showAddCategory, setShowAddCategory] = useState(false);

  const handleAddClause = async (categoryId: string) => {
    const text = (newClauseTexts[categoryId] || "").trim();
    if (!text) return;
    const cat = categories.find(c => c.id === categoryId);

    // Non-admins can still add a one-off clause to this quote only — append the
    // text to the current value without writing to the shared library.
    if (!isAdmin) {
      const combined = clauseIdsToText(selectedClauseIds);
      onChange(combined ? `${combined}\n• ${text}` : `• ${text}`);
      setNewClauseTexts(p => ({ ...p, [categoryId]: "" }));
      toast.success(`Added to ${cat?.name ?? "this quote"} (this quote only).`);
      return;
    }

    const lastSort = cat ? cat.clauses.length : 0;
    const { data, error } = await (supabase as any)
      .from("terms_clauses")
      .insert({ category_id: categoryId, text, sort_order: lastSort + 1, is_active: true })
      .select("id")
      .single();
    if (error) {
      toast.error(error.message || "Failed to add clause");
      return;
    }
    setNewClauseTexts(p => ({ ...p, [categoryId]: "" }));
    invalidateLibrary();
    // Auto-select the new clause
    if (data?.id) {
      const nextSelected = [...selectedClauseIds, data.id];
      setSelectedClauseIds(nextSelected);
      // We don't yet have the clause in `categories` cache, so prepend its text manually.
      const combined = clauseIdsToText(nextSelected.filter(id => id !== data.id));
      onChange(combined ? `${combined}\n• ${text}` : `• ${text}`);
    }
    toast.success("Clause added to library");
  };

  const slugify = (s: string) =>
    s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");

  const handleAddCategory = async () => {
    const name = newCategoryName.trim();
    if (!name) return;
    const sort_order = (categories[categories.length - 1] as any)?.sort_order
      ? (categories[categories.length - 1] as any).sort_order + 1
      : categories.length + 1;
    const { error } = await (supabase as any)
      .from("terms_categories")
      .insert({ name, slug: slugify(name) || `cat_${Date.now()}`, sort_order, is_active: true });
    if (error) {
      toast.error(error.message || "Failed to add category");
      return;
    }
    setNewCategoryName("");
    setShowAddCategory(false);
    invalidateLibrary();
    toast.success("Category added");
  };

  const getDefaultClauseIds = useMemo(
    () => () => categories.flatMap(cat => cat.clauses.filter(c => c.isDefault).map(c => c.id)),
    [categories]
  );
  const clauseIdsToText = useMemo(
    () => (selectedIds: string[]) => {
      const texts: string[] = [];
      for (const cat of categories) {
        for (const cl of cat.clauses) {
          if (selectedIds.includes(cl.id)) texts.push(`• ${cl.text}`);
        }
      }
      return texts.join("\n");
    },
    [categories]
  );

  const [selectedClauseIds, setSelectedClauseIds] = useState<string[]>([]);
  const [isManualMode, setIsManualMode] = useState(false);
  const [isOpen, setIsOpen] = useState(true);
  // Default-expand all categories so the clauses + Add Clause inputs are visible immediately
  const [accordionOpen, setAccordionOpen] = useState<string[]>([]);
  const [hasInitialized, setHasInitialized] = useState(false);

  // When the categories load (or change), open them all by default
  useEffect(() => {
    if (categories.length > 0 && accordionOpen.length === 0) {
      setAccordionOpen(categories.map(c => c.id));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories.length]);

  // Initialize with defaults on first mount (only if value is empty)
  useEffect(() => {
    if (!hasInitialized && !isLoading) {
      if (!value || value.trim() === "") {
        // No existing value - use defaults
        const defaults = getDefaultClauseIds();
        setSelectedClauseIds(defaults);
        onChange(clauseIdsToText(defaults));
      } else {
        // Has existing value - switch to manual mode to preserve it
        setIsManualMode(true);
      }
      setHasInitialized(true);
    }
  }, [hasInitialized, isLoading, value, onChange, getDefaultClauseIds, clauseIdsToText]);

  const handleClauseToggle = (clauseId: string, checked: boolean) => {
    const newSelected = checked
      ? [...selectedClauseIds, clauseId]
      : selectedClauseIds.filter(id => id !== clauseId);
    
    setSelectedClauseIds(newSelected);
    onChange(clauseIdsToText(newSelected));
  };

  const handleSelectAllInCategory = (categoryId: string) => {
    const category = categories.find(c => c.id === categoryId);
    if (!category) return;
    
    const categoryClauseIds = category.clauses.map(c => c.id);
    const newSelected = [...new Set([...selectedClauseIds, ...categoryClauseIds])];
    
    setSelectedClauseIds(newSelected);
    onChange(clauseIdsToText(newSelected));
  };

  const handleClearCategory = (categoryId: string) => {
    const category = categories.find(c => c.id === categoryId);
    if (!category) return;
    
    const categoryClauseIds = new Set(category.clauses.map(c => c.id));
    const newSelected = selectedClauseIds.filter(id => !categoryClauseIds.has(id));
    
    setSelectedClauseIds(newSelected);
    onChange(clauseIdsToText(newSelected));
  };

  const toggleMode = () => {
    if (isManualMode) {
      // Switching back to selector mode - reset to current selections
      onChange(clauseIdsToText(selectedClauseIds));
    }
    setIsManualMode(!isManualMode);
  };

  const selectedCount = selectedClauseIds.length;

  if (isManualMode) {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Manual Edit Mode</span>
          <Button 
            type="button" 
            variant="ghost" 
            size="sm" 
            onClick={toggleMode}
            className="h-7 text-xs"
          >
            <List className="h-3 w-3 mr-1" />
            Use Clause Selector
          </Button>
        </div>
        <Textarea 
          value={value} 
          onChange={(e) => onChange(e.target.value)} 
          rows={6}
          placeholder="Enter terms and conditions..."
        />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <div className="flex items-center justify-between">
          <CollapsibleTrigger className="flex items-center gap-2 text-sm font-medium hover:text-primary transition-colors">
            <ChevronDown className={cn(
              "h-4 w-4 transition-transform",
              isOpen ? "" : "-rotate-90"
            )} />
            Select Clauses ({selectedCount} selected)
          </CollapsibleTrigger>
          <div className="flex items-center gap-1">
            {isAdmin && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => window.open("/admin/terms-library", "_blank")}
                className="h-7 text-xs"
                title="Open the Terms & Conditions library admin page"
              >
                <ExternalLink className="h-3 w-3 mr-1" />
                Manage library
              </Button>
            )}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={toggleMode}
              className="h-7 text-xs"
            >
              <Edit3 className="h-3 w-3 mr-1" />
              Edit Manually
            </Button>
          </div>
        </div>

        <CollapsibleContent className="mt-2">
          <div className="border rounded-md max-h-[300px] overflow-y-auto">
            {isLoading && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground p-3">
                <Loader2 className="h-3 w-3 animate-spin" /> Loading clauses…
              </div>
            )}
            <Accordion
              type="multiple"
              className="w-full"
              value={accordionOpen}
              onValueChange={setAccordionOpen}
            >
              {categories.map((category) => {
                const categorySelectedCount = category.clauses.filter(
                  c => selectedClauseIds.includes(c.id)
                ).length;
                
                return (
                  <AccordionItem 
                    key={category.id} 
                    value={category.id}
                    className="border-b last:border-b-0"
                  >
                    <AccordionTrigger className="px-3 py-2 text-sm hover:no-underline">
                      <div className="flex items-center gap-2">
                        <span>{category.name}</span>
                        {categorySelectedCount > 0 && (
                          <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded-full">
                            {categorySelectedCount}
                          </span>
                        )}
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-3 pb-3">
                      <div className="flex gap-2 mb-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 text-xs"
                          onClick={() => handleSelectAllInCategory(category.id)}
                        >
                          Select All
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 text-xs"
                          onClick={() => handleClearCategory(category.id)}
                        >
                          Clear
                        </Button>
                      </div>
                      <div className="space-y-2">
                        {category.clauses.map((clause) => (
                          <label 
                            key={clause.id} 
                            className="flex items-start gap-2 cursor-pointer group"
                          >
                            <Checkbox
                              checked={selectedClauseIds.includes(clause.id)}
                              onCheckedChange={(checked) => 
                                handleClauseToggle(clause.id, checked === true)
                              }
                              className="mt-0.5"
                            />
                            <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                              {clause.text}
                              {clause.isDefault && (
                                <span className="ml-1 text-xs text-primary">(default)</span>
                              )}
                            </span>
                          </label>
                        ))}
                      </div>
                      <div className="flex items-center gap-2 mt-3 pt-3 border-t">
                          <Input
                            value={newClauseTexts[category.id] || ""}
                            onChange={(e) =>
                              setNewClauseTexts((p) => ({ ...p, [category.id]: e.target.value }))
                            }
                            placeholder={
                              isAdmin
                                ? `Add new clause to ${category.name} library…`
                                : `Add a one-off clause to ${category.name} (this quote only)…`
                            }
                            className="h-8 text-sm"
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                handleAddClause(category.id);
                              }
                            }}
                          />
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => handleAddClause(category.id)}
                            disabled={!(newClauseTexts[category.id] || "").trim()}
                            className="h-8"
                          >
                            <Plus className="h-3 w-3 mr-1" /> Add
                          </Button>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
            {isAdmin && (
              <div className="border-t p-2">
                {showAddCategory ? (
                  <div className="flex items-center gap-2">
                    <Input
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      placeholder="New category name (e.g. Group Bookings)"
                      className="h-8 text-sm"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddCategory();
                        }
                      }}
                    />
                    <Button type="button" size="sm" onClick={handleAddCategory} disabled={!newCategoryName.trim()} className="h-8">
                      Add
                    </Button>
                    <Button type="button" size="sm" variant="ghost" onClick={() => { setShowAddCategory(false); setNewCategoryName(""); }} className="h-8">
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowAddCategory(true)}
                    className="h-7 text-xs w-full justify-start"
                  >
                    <Plus className="h-3 w-3 mr-1" /> Add new category
                  </Button>
                )}
              </div>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Preview */}
      {selectedCount > 0 && (
        <div className="p-3 bg-muted rounded-md">
          <p className="text-xs font-medium text-muted-foreground mb-2">Preview:</p>
          <div className="text-sm whitespace-pre-line max-h-[100px] overflow-y-auto">
            {value}
          </div>
        </div>
      )}
    </div>
  );
}
